/**
 * useWorkspaceSession — React hook that manages a workspace voice session.
 *
 * Connects to the workspace runtime at ws://<host>/workspace-ws,
 * handles the event protocol (see configural-workspace-runtime/src/
 * workspace/workspace-gateway.js) to update canvas + transcript state,
 * captures mic audio via AudioWorklet, plays back agent TTS.
 *
 * Exposes:
 *   status         — "idle" | "connecting" | "ready" | "live" | "ended" | "error"
 *   transcript     — [{role:"agent"|"patient", text, t}]
 *   views          — Canvas view stack (from ui_render / ui_patch / ui_clear)
 *   start(options) — open WS, start mic, send configure
 *   stop()         — tear down WS + audio
 *   sendAction(a)  — forward a client action up to the agent
 *
 * Audio pipeline assumes the existing /pcm-processor.js worklet is
 * served from /public — same as the voice call tab.
 */

import { useCallback, useEffect, useRef, useState } from "react";

// Where the workspace runtime lives. Configurable via env or the
// UI picker; falls back to localhost:3009 for local dev.
function defaultWorkspaceWsUrl() {
  if (typeof window === "undefined") return "ws://localhost:3009/workspace-ws";
  // If the admin is served on https, upgrade; otherwise use ws:// to localhost:3009.
  const envUrl = import.meta?.env?.VITE_WORKSPACE_WS_URL;
  if (envUrl) return envUrl;
  return "ws://localhost:3009/workspace-ws";
}

function arrayBufferToBase64(buf) {
  let binary = "";
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return typeof btoa === "function" ? btoa(binary) : Buffer.from(binary, "binary").toString("base64");
}

// ─── Diagnostic logging ─────────────────────────────────────────────
// All logs tagged [WS-CLIENT]. Filter in DevTools with that token.
// Audio-chunk logs tagged [WS-AUDIO] so you can isolate playback timing.
// Toggle verbosity via localStorage.setItem("workspace_debug", "1") in
// the browser console, or by setting WORKSPACE_DEBUG=true before importing.
function debugOn() {
  try { return typeof localStorage !== "undefined" && localStorage.getItem("workspace_debug") !== "0"; }
  catch { return true; }
}
const log = (...args) => { if (debugOn()) console.log("[WS-CLIENT]", ...args); };
const logAudio = (...args) => { if (debugOn()) console.log("[WS-AUDIO]", ...args); };
const warn = (...args) => console.warn("[WS-CLIENT]", ...args);
const err = (...args) => console.error("[WS-CLIENT]", ...args);

export default function useWorkspaceSession() {
  const [status, setStatus] = useState("idle");
  const [transcript, setTranscript] = useState([]);
  const [views, setViews] = useState([]);
  const [error, setError] = useState(null);

  const wsRef = useRef(null);
  const audioCtxRef = useRef(null);
  const streamRef = useRef(null);
  const workletRef = useRef(null);
  const playerRef = useRef(null);

  // Mutable views state for patch lookup during updates.
  const viewsRef = useRef([]);
  useEffect(() => { viewsRef.current = views; }, [views]);

  const appendTranscript = useCallback((role, text) => {
    if (!text || !text.trim()) return;
    setTranscript((prev) => {
      // Merge consecutive same-role additions so streaming partials coalesce.
      const last = prev[prev.length - 1];
      if (last && last.role === role) {
        if (text.startsWith(last.text)) return [...prev.slice(0, -1), { ...last, text }];
        if (text === last.text) return prev;
        if (last.text.includes(text)) return prev;
        return [...prev.slice(0, -1), { ...last, text: last.text + " " + text }];
      }
      return [...prev, { role, text, t: Date.now() }];
    });
  }, []);

  const pushView = useCallback((name, data) => {
    setViews((prev) => {
      // If the same view is already on top, replace it; else push.
      const top = prev[prev.length - 1];
      if (top && top.name === name) {
        return [...prev.slice(0, -1), { ...top, data: data || {} }];
      }
      return [...prev, { id: `${name}-${Date.now().toString(36)}`, name, data: data || {} }];
    });
  }, []);

  const patchView = useCallback((name, patch) => {
    setViews((prev) => {
      // Find the topmost view with this name and merge.
      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i].name === name) {
          const merged = { ...prev[i].data, ...(patch || {}) };
          // Special-case: `values` object should deep-merge, not replace.
          if (patch?.values && prev[i].data?.values) {
            merged.values = { ...prev[i].data.values, ...patch.values };
          }
          return [...prev.slice(0, i), { ...prev[i], data: merged }, ...prev.slice(i + 1)];
        }
      }
      // No existing view — push a new one.
      return [...prev, { id: `${name}-${Date.now().toString(36)}`, name, data: patch || {} }];
    });
  }, []);

  const clearView = useCallback((name) => {
    setViews((prev) => prev.filter((v) => v.name !== name));
  }, []);

  // Audio telemetry counters — live across the session for end-of-session summary.
  const audioStatsRef = useRef({ chunks: 0, bytes: 0, firstAt: 0, lastAt: 0 });
  const lastAudioArrivalRef = useRef(0);

  const handleMessage = useCallback(async (raw) => {
    let msg;
    try { msg = JSON.parse(raw); } catch {
      warn("received non-JSON WS message, ignoring (len=" + raw.length + ")");
      return;
    }

    // Log every non-audio event at a low level. Audio is per-chunk below.
    const isAudio = msg.type === "server_content" && !!msg.audio?.data;
    if (!isAudio) {
      const summary = { type: msg.type };
      if (msg.type === "server_content") {
        if (msg.outputTranscription) summary.outputTranscription = String(msg.outputTranscription).slice(0, 80);
        if (msg.inputTranscription)  summary.inputTranscription  = String(msg.inputTranscription).slice(0, 80);
        if (msg.turnComplete)        summary.turnComplete = true;
        if (msg.ui_render)           summary.ui_render = msg.ui_render.view;
        if (msg.ui_patch)            summary.ui_patch = { view: msg.ui_patch.view, keys: Object.keys(msg.ui_patch.patch || {}) };
        if (msg.ui_clear)            summary.ui_clear = msg.ui_clear.view;
        if (msg.cost)                summary.cost = msg.cost;
      }
      if (msg.type === "tool_call")    summary.tool = msg.tool;
      if (msg.type === "error")        summary.error = msg.error;
      if (msg.type === "session_ended") summary.reason = msg.reason;
      log("recv", summary);
    }

    if (msg.type === "ready") {
      log("session ready — sessionId=" + msg.sessionId);
      setStatus("ready");
      return;
    }

    if (msg.type === "error") {
      err("server error:", msg.error);
      setError(msg.error || "unknown");
      setStatus("error");
      return;
    }

    if (msg.type === "session_ended") {
      const s = audioStatsRef.current;
      const totalSec = s.firstAt ? (s.lastAt - s.firstAt) / 1000 : 0;
      log(`session ended (${msg.reason || "complete"}) — audio stats: ${s.chunks} chunks, ${(s.bytes/1024).toFixed(1)}KB, ${totalSec.toFixed(2)}s span`);
      setStatus("ended");
      appendTranscript("system", `Session ended (${msg.reason || "complete"})`);
      return;
    }

    // server_content envelope carries audio / transcripts / UI events
    if (msg.type === "server_content") {
      // Audio: pipeline provider sends the PCM on msg.audio.data AND
      // ALSO embeds the same data inside msg.raw.serverContent.modelTurn.parts[].inlineData
      // for legacy Gemini-shape consumers. Playing both causes every
      // chunk to overlap itself (observed as duplicate/broken speech).
      // Play ONLY the flat msg.audio.data path — that's the canonical
      // one for the pipeline provider.
      if (msg.audio?.data) {
        const now = performance.now();
        const s = audioStatsRef.current;
        const b64Len = msg.audio.data.length;
        const approxBytes = Math.floor(b64Len * 3 / 4);     // rough: base64 → raw size
        const approxSamples = Math.floor(approxBytes / 2);   // 16-bit PCM
        const approxMs = (approxSamples / 24000) * 1000;     // 24kHz sample rate
        const sinceLast = lastAudioArrivalRef.current ? (now - lastAudioArrivalRef.current).toFixed(1) : "first";
        lastAudioArrivalRef.current = now;
        s.chunks += 1;
        s.bytes  += approxBytes;
        if (!s.firstAt) s.firstAt = now;
        s.lastAt = now;

        // Measure how much audio is currently queued ahead of `now`.
        const ctx = playerRef.current?._ctx;
        const queuedMs = ctx && playerRef.current?._nextStartTime
          ? Math.max(0, (playerRef.current._nextStartTime - ctx.currentTime) * 1000)
          : 0;
        logAudio(
          `chunk #${s.chunks}  size=${approxBytes}B  dur≈${approxMs.toFixed(0)}ms  ` +
          `sinceLast=${sinceLast}ms  queuedAhead=${queuedMs.toFixed(0)}ms`
        );

        try {
          playerRef.current?.play(msg.audio.data);
        } catch (e) {
          err("playerRef.play() threw:", e);
        }
      }
      if (msg.inputTranscription) {
        log("STT (patient) → " + String(msg.inputTranscription).slice(0, 80));
        appendTranscript("patient", msg.inputTranscription);
      }
      if (msg.outputTranscription) {
        log("TTS text (agent) → " + String(msg.outputTranscription).slice(0, 80));
        appendTranscript("agent", msg.outputTranscription);
      }
      if (msg.turnComplete) {
        log("turnComplete");
      }
      if (msg.ui_render) {
        log("ui_render view=" + msg.ui_render.view, msg.ui_render.data);
        pushView(msg.ui_render.view, msg.ui_render.data);
      }
      if (msg.ui_patch) {
        log("ui_patch view=" + msg.ui_patch.view, msg.ui_patch.patch);
        patchView(msg.ui_patch.view, msg.ui_patch.patch);
      }
      if (msg.ui_clear) {
        log("ui_clear view=" + msg.ui_clear.view);
        clearView(msg.ui_clear.view);
      }
      return;
    }

    if (msg.type === "tool_call") {
      log("tool_call " + msg.tool + "(" + JSON.stringify(msg.args || {}).slice(0, 100) + ")");
      return;
    }
  }, [appendTranscript, pushView, patchView, clearView]);

  // Mic telemetry
  const micStatsRef = useRef({ chunks: 0, bytes: 0, firstAt: 0, lastAt: 0 });

  const startMic = useCallback(async () => {
    log("requesting mic (echoCancellation=true, noiseSuppression=true, autoGainControl=true, sr=16000)");
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, sampleRate: 16000 },
    });
    const tracks = stream.getAudioTracks();
    log("mic stream acquired — tracks=" + tracks.length + ", label=" + (tracks[0]?.label || "?"));
    // Log the actual settings the browser gave us (might differ from the request)
    try {
      const s = tracks[0]?.getSettings?.() || {};
      log("mic settings:", {
        deviceId: s.deviceId ? s.deviceId.slice(0, 10) + "…" : null,
        sampleRate: s.sampleRate, channelCount: s.channelCount,
        echoCancellation: s.echoCancellation, noiseSuppression: s.noiseSuppression,
        autoGainControl: s.autoGainControl,
      });
    } catch {}
    streamRef.current = stream;
    const ctx = new AudioContext({ sampleRate: 16000 });
    audioCtxRef.current = ctx;
    log("input AudioContext created — sampleRate=" + ctx.sampleRate + " (requested 16000), state=" + ctx.state);
    await ctx.audioWorklet.addModule("/pcm-processor.js");
    const src = ctx.createMediaStreamSource(stream);
    const wk = new AudioWorkletNode(ctx, "pcm-processor");
    workletRef.current = wk;
    wk.port.postMessage({ noiseThreshold: 0.003 });
    micStatsRef.current = { chunks: 0, bytes: 0, firstAt: 0, lastAt: 0 };
    wk.port.onmessage = (e) => {
      if (wsRef.current?.readyState !== WebSocket.OPEN) return;
      const now = performance.now();
      const ms = micStatsRef.current;
      ms.chunks += 1;
      ms.bytes += e.data?.byteLength || 0;
      if (!ms.firstAt) ms.firstAt = now;
      ms.lastAt = now;
      // Log every 100 chunks (~roughly every 3–5 seconds, depending on chunk size).
      if (ms.chunks === 1 || ms.chunks % 100 === 0) {
        log(`mic chunk #${ms.chunks}  totalBytes=${(ms.bytes/1024).toFixed(1)}KB  avgRate=${(ms.bytes/(ms.lastAt-ms.firstAt)*1000/1024).toFixed(1)}KB/s`);
      }
      wsRef.current.send(JSON.stringify({
        type: "audio",
        data: arrayBufferToBase64(e.data),
      }));
    };
    src.connect(wk);
    log("mic pipeline connected — worklet is streaming");
  }, []);

  const start = useCallback(async (options = {}) => {
    log("start() called with options:", options);
    setError(null);
    setViews([]);
    setTranscript([]);
    setStatus("connecting");
    audioStatsRef.current = { chunks: 0, bytes: 0, firstAt: 0, lastAt: 0 };
    lastAudioArrivalRef.current = 0;

    try {
      const { AudioPlayer } = await import("../audio/playback.js");
      playerRef.current = new AudioPlayer();
      playerRef.current.setPlaybackRate(1.0);
      const outCtx = playerRef.current._ctx;
      log("AudioPlayer created — ctx.sampleRate=" + (outCtx?.sampleRate || "?") + ", ctx.state=" + (outCtx?.state || "?"));
    } catch (e) {
      err("audio player load failed:", e);
      setError(`audio player load failed: ${e.message}`);
      setStatus("error");
      return;
    }

    const wsUrl = options.wsUrl || defaultWorkspaceWsUrl();
    log("opening WS to " + wsUrl);
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      const configMsg = {
        type: "configure",
        workflow: options.workflow || "referral-intake",
        tenantId: options.tenantId || null,
        subTenantId: options.subTenantId || null,
        voice: options.voice || "en-US-Neural2-F",
        textModel: options.textModel || "gemini-2.5-flash",
        stt_provider: options.stt_provider || "google",
        tts_provider: options.tts_provider || "google",
        practiceName: options.practiceName || "the clinic",
      };
      log("WS open — sending configure:", configMsg);
      ws.send(JSON.stringify(configMsg));
      setStatus("live");
      startMic().catch((e) => {
        err("mic failed:", e);
        setError(`mic failed: ${e.message}`);
        setStatus("error");
      });
    };
    ws.onmessage = (e) => handleMessage(e.data);
    ws.onerror = (e) => { err("websocket error", e); setError("websocket error"); setStatus("error"); };
    ws.onclose = (e) => {
      log("WS closed — code=" + e.code + ", reason=" + (e.reason || "(none)"));
      setStatus((s) => (s === "ended" || s === "error" ? s : "idle"));
    };
  }, [handleMessage, startMic]);

  const stop = useCallback(() => {
    const a = audioStatsRef.current, m = micStatsRef.current;
    if (a.chunks || m.chunks) {
      log(
        `stop() — audio RX: ${a.chunks} chunks, ${(a.bytes/1024).toFixed(1)}KB, ` +
        `span=${((a.lastAt-a.firstAt)/1000).toFixed(2)}s | ` +
        `mic TX: ${m.chunks} chunks, ${(m.bytes/1024).toFixed(1)}KB, ` +
        `span=${((m.lastAt-m.firstAt)/1000).toFixed(2)}s`
      );
    }
    try { wsRef.current?.close(); } catch {}
    wsRef.current = null;
    try { audioCtxRef.current?.close(); } catch {}
    audioCtxRef.current = null;
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    streamRef.current = null;
    try { playerRef.current?.stop(); } catch {}
    setStatus((s) => (s === "ended" || s === "error" ? s : "idle"));
  }, []);

  const sendAction = useCallback((action) => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) return;
    log("→ client_action:", action);
    wsRef.current.send(JSON.stringify({ type: "client_action", payload: action }));
  }, []);

  useEffect(() => () => stop(), [stop]);

  return { status, transcript, views, error, start, stop, sendAction };
}

function safeJsonGet(s, key) {
  try { const o = JSON.parse(s); return o?.[key]; } catch { return null; }
}
